import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';
import { Layers, Database, Shield, Zap, ArrowRight, Github, Mail } from 'lucide-react';

import Navbar from '../components/Navbar';

export default function Solutions() {
  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="pt-48 pb-24 px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="max-w-3xl"
            >
              <h1 className="text-6xl md:text-8xl font-mono uppercase tracking-tighter mb-8 leading-[0.9]">
                Architectural <br/> <span className="text-[#0074D9]">Integrity.</span>
              </h1>
              <p className="text-zinc-500 text-xl font-light leading-relaxed">
                Yacht Labs provides the solutions needed to build production-grade applications that scale from the first user to millions.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 px-8 bg-zinc-950 border-y border-white/5">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="p-12 border border-zinc-900 bg-black rounded-3xl space-y-8">
              <div className="w-12 h-12 bg-[#001F3F] border border-[#0074D9]/30 rounded-2xl flex items-center justify-center text-[#0074D9]">
                <Database size={24} />
              </div>
              <h3 className="text-2xl font-mono uppercase tracking-tight">Full-Stack Synthesis</h3>
              <p className="text-zinc-500 font-light leading-relaxed">
                Synthesis isn't just about UI. Yacht generates the data models, API endpoints, and serverless logic required for complex full-stack applications.
              </p>
              <ul className="space-y-4 text-xs font-mono uppercase tracking-widest text-zinc-600">
                <li className="flex items-center gap-3"><Zap size={14} className="text-[#0074D9]" /> Auto-provisioned Databases</li>
                <li className="flex items-center gap-3"><Zap size={14} className="text-[#0074D9]" /> Real-time Auth Integration</li>
                <li className="flex items-center gap-3"><Zap size={14} className="text-[#0074D9]" /> Serverless Logic Engine</li>
              </ul>
            </div>

            <div className="p-12 border border-zinc-900 bg-black rounded-3xl space-y-8">
              <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center text-white">
                <Shield size={24} />
              </div>
              <h3 className="text-2xl font-mono uppercase tracking-tight">Enterprise Security</h3>
              <p className="text-zinc-500 font-light leading-relaxed">
                Security shouldn't be an afterthought. Yacht synthesizes applications with best-practice security rules and data isolation policies baked in.
              </p>
              <ul className="space-y-4 text-xs font-mono uppercase tracking-widest text-zinc-600">
                <li className="flex items-center gap-3"><Shield size={14} className="text-white" /> RBAC Policy Synthesis</li>
                <li className="flex items-center gap-3"><Shield size={14} className="text-white" /> Data Encryption at Rest</li>
                <li className="flex items-center gap-3"><Shield size={14} className="text-white" /> Automated Vulnerability Scans</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-32 px-8">
           <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-24">
              <div className="flex-1 space-y-8">
                <h2 className="tech-label">The Stack</h2>
                <h3 className="text-4xl font-mono uppercase tracking-tighter">Modern Industry Standards. <span className="text-[#0074D9]">No Magic.</span></h3>
                <p className="text-zinc-400 text-lg font-light leading-relaxed">
                  We build on the tools you already know and trust. Yacht synthesizes high-quality React code, Tailwind CSS utility classes, and Firebase backends.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="px-4 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-500 uppercase tracking-widest">TypeScript</div>
                  <div className="px-4 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Tailwind CSS</div>
                  <div className="px-4 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-500 uppercase tracking-widest">PostgreSQL</div>
                  <div className="px-4 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Vite</div>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                 <div className="h-48 bg-zinc-900 rounded-3xl border border-zinc-800" />
                 <div className="h-48 bg-[#0074D9]/10 rounded-3xl border border-[#0074D9]/20 flex items-center justify-center">
                    <Layers className="text-[#0074D9]" size={48} />
                 </div>
                 <div className="col-span-2 h-48 bg-black border border-zinc-800 rounded-3xl" />
              </div>
           </div>
        </section>
      </main>

      <footer className="p-24 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-24">
          <div className="col-span-2 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <YachtLogo className="w-8 h-8" />
              <span className="font-mono text-2xl uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-zinc-500 font-light leading-relaxed max-w-sm">
              Synthesizing the future of digital architecture. Empowering founders to bridge the gap between intent and existence.
            </p>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Platform</h4>
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Synthesis</Link>
              <Link to="/use-cases" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Use Cases</Link>
              <Link to="/solutions" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Solutions</Link>
              <Link to="/pricing" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
            </div>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Connect</h4>
            <div className="flex flex-col gap-4">
              <a href="#" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Github size={14} /> GitHub
              </a>
              <a href="mailto:hello@yacht-labs.com" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Mail size={14} /> Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
